import { useCallback, useMemo, useState } from 'react'
import { createMessageApiService, notifyConversationsChanged } from '@gorilla-works/messages'

import { useAuth } from '@/contexts/auth-context'
import { useToast } from '@/hooks/use-toast'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { env } from '@/lib/env'
import { resolveMessageConversationKey } from '@/lib/message-conversation-key'
import { emitStageMessagesChange } from '@/lib/stage-message-events'

/** The endpoint rejects more than this per call. */
const MAX_ITEMS_PER_CALL = 100

/**
 * Mark read / unread for one row or a whole selection.
 *
 * Lives in a hook rather than in either menu because both the selection bar and the row kebab need
 * it, and duplicating the key resolution is how the two would drift. The menus stay presentational:
 * they ask `canMarkMessages` whether to render, and call `markMessages` when clicked.
 */
export function useMessageReadActions(entityType: string, items: Array<Record<string, any>>) {
  const { token } = useAuth()
  const { toast } = useToast()
  const { trackWorkflowEvent } = useWorkflowTracking()
  const [isMarking, setIsMarking] = useState(false)
  const [isMuting, setIsMuting] = useState(false)

  const byId = useMemo(
    () => new Map(items.map((item) => [String(item?.id), item])),
    [items]
  )

  /** The conversation key for one row, or null when it has no thread to address. */
  const resolveKey = useCallback(
    (rowOrId: string | Record<string, any>) => {
      const row = typeof rowOrId === 'string' ? byId.get(rowOrId) : rowOrId
      return row ? resolveMessageConversationKey(entityType, row) : null
    },
    [byId, entityType]
  )

  /**
   * Whether any of these rows has a conversation at all — the same rule that decides whether the
   * Message column renders a trigger. Menus use it to stay hidden rather than offering an action
   * that would do nothing.
   */
  const canMarkMessages = useCallback(
    (rowsOrIds: Array<string | Record<string, any>>) => rowsOrIds.some((row) => resolveKey(row) !== null),
    [resolveKey]
  )

  const markMessages = useCallback(
    async (rowsOrIds: Array<string | Record<string, any>>, read: boolean) => {
      const keys = rowsOrIds
        .map(resolveKey)
        .filter((key): key is { entityId: string; entityTypeId: string } => key !== null)

      // Rows with no key have no thread — VmsEmail and PreOnboardRequest have no Message field
      // config seeded, and a request row may never have been linked to one.
      const withoutThread = rowsOrIds.length - keys.length

      if (keys.length === 0) {
        toast({
          title: 'Nothing to update',
          description: 'None of the selected rows have a message thread.',
        })
        return
      }

      // Grouped by the RESOLVED entity type, not the tab's: the endpoint scopes one call to one
      // type, and within the ClientRequest tab the type varies per row.
      const grouped = new Map<string, string[]>()
      keys.forEach(({ entityTypeId, entityId }) => {
        const ids = grouped.get(entityTypeId)
        if (ids) ids.push(entityId)
        else grouped.set(entityTypeId, [entityId])
      })

      setIsMarking(true)
      try {
        const api = createMessageApiService({
          apiEndpoint: env.messagesApiUrl,
          userToken: () => token || '',
        })

        let changed = 0
        let notSubscribed = 0
        let noConversation = 0

        for (const [entityTypeId, entityIds] of grouped) {
          // Page size reaches 100 and the selection is not cleared when the page changes, so a
          // two-page selection would otherwise take a 400.
          for (let offset = 0; offset < entityIds.length; offset += MAX_ITEMS_PER_CALL) {
            const chunk = entityIds.slice(offset, offset + MAX_ITEMS_PER_CALL)
            const result = await api.markConversationRead({
              entityTypeId,
              read,
              items: chunk.map((entityId) => ({ entityId })),
            })

            changed += (result.markedRead ?? 0) + (result.markedUnread ?? 0)
            notSubscribed += result.notSubscribed ?? 0
            noConversation += result.noConversation ?? 0
          }

          // Tell the mounted per-row badges to re-read. They coalesce, so a page's worth of
          // notifications costs one request rather than one each.
          notifyConversationsChanged(entityTypeId, entityIds)
          // And the per-stage tab badges: a mark-read zeroes those assignments instantly, a mark-unread
          // reconciles from the server (no safe optimistic value for the new unread count).
          emitStageMessagesChange({ entityTypeId, entityIds, optimisticRead: read })
        }

        trackWorkflowEvent('Click Tracking', {
          action: read ? 'bulk_mark_read' : 'bulk_mark_unread',
          requested: keys.length,
          changed,
        })

        // notSubscribed is not an error: those are threads the caller can open but was never a
        // participant in, so they carried no unread signal to change. Saying so beats a silent no-op.
        // noConversation is the common one and was missing: most Assignment rows have no
        // conversation document until somebody posts, so a selection of untouched rows reports
        // "0 updated" and would otherwise look broken rather than empty.
        const notes = [
          notSubscribed > 0 ? `${notSubscribed} skipped (not a participant)` : null,
          noConversation > 0 ? `${noConversation} with no conversation yet` : null,
          withoutThread > 0 ? `${withoutThread} with no message thread` : null,
        ].filter(Boolean)

        toast({
          title: read ? 'Marked as read' : 'Marked as unread',
          description: [`${changed} updated`, ...notes].join(' · '),
        })
      } catch (error) {
        toast({
          title: read ? 'Could not mark as read' : 'Could not mark as unread',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsMarking(false)
      }
    },
    [resolveKey, token, toast, trackWorkflowEvent]
  )

  /**
   * Mute or unmute a selection — whether these threads send the caller updates from now on.
   *
   * <p>Lives beside {@link markMessages} because it needs the identical key resolution, per-type
   * grouping and 100-item chunking, and a second copy of that is how the two would drift. It is a
   * genuinely different act, though: marking read clears a signal that already arrived, muting stops
   * future ones. Neither touches access — a muted thread is still fully readable from its record.</p>
   *
   * <p><b>A 403 means nothing was written for that whole chunk.</b> The server refuses the entire batch
   * on the first inaccessible conversation rather than reporting per item, because a per-item answer
   * would reveal which entity ids exist. So this reports the failure and does not claim partial
   * success.</p>
   */
  const muteMessages = useCallback(
    async (rowsOrIds: Array<string | Record<string, any>>, subscribed: boolean) => {
      const keys = rowsOrIds
        .map(resolveKey)
        .filter((key): key is { entityId: string; entityTypeId: string } => key !== null)

      const withoutThread = rowsOrIds.length - keys.length

      if (keys.length === 0) {
        toast({
          title: 'Nothing to update',
          description: 'None of the selected rows have a message thread.',
        })
        return
      }

      const grouped = new Map<string, string[]>()
      keys.forEach(({ entityTypeId, entityId }) => {
        const ids = grouped.get(entityTypeId)
        if (ids) ids.push(entityId)
        else grouped.set(entityTypeId, [entityId])
      })

      setIsMuting(true)
      try {
        const api = createMessageApiService({
          apiEndpoint: env.messagesApiUrl,
          userToken: () => token || '',
        })

        let changed = 0
        let unchanged = 0
        let noConversation = 0

        for (const [entityTypeId, entityIds] of grouped) {
          for (let offset = 0; offset < entityIds.length; offset += MAX_ITEMS_PER_CALL) {
            const chunk = entityIds.slice(offset, offset + MAX_ITEMS_PER_CALL)
            const result = await api.setConversationSubscriptionBulk({
              entityTypeId,
              subscribed,
              items: chunk.map((entityId) => ({ entityId })),
            })

            changed += result.changed ?? 0
            unchanged += result.unchanged ?? 0
            noConversation += result.noConversation ?? 0
          }

          // The bell in the panel header and the row badge both read subscription state.
          notifyConversationsChanged(entityTypeId, entityIds)
          // Muting changes whether these count toward the stage badges — reconcile from the server.
          emitStageMessagesChange({ entityTypeId, entityIds })
        }

        trackWorkflowEvent('Click Tracking', {
          action: subscribed ? 'bulk_unmute' : 'bulk_mute',
          requested: keys.length,
          changed,
        })

        const notes = [
          unchanged > 0 ? `${unchanged} already ${subscribed ? 'unmuted' : 'muted'}` : null,
          noConversation > 0 ? `${noConversation} with no conversation yet` : null,
          withoutThread > 0 ? `${withoutThread} with no message thread` : null,
        ].filter(Boolean)

        toast({
          title: subscribed ? 'Unmuted' : 'Muted',
          description: [`${changed} updated`, ...notes].join(' · '),
        })
      } catch (error) {
        toast({
          title: subscribed ? 'Could not unmute' : 'Could not mute',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsMuting(false)
      }
    },
    [resolveKey, token, toast, trackWorkflowEvent]
  )

  return { canMarkMessages, markMessages, isMarking, muteMessages, isMuting }
}
